(function () {
  'use strict';

  if (window.MCubeProjects) return;
  if (!window.MCubeCore) return;

  var core = window.MCubeCore;
  var emitter = core.createEmitter();
  var STORAGE_KEY = core.STORAGE_KEYS.projects;
  var CHANGE_EVENT = 'mcube:projects:change';
  var initialState = {
    version: core.STORAGE_VERSION,
    updatedAt: 0,
    projects: [],
    tombstones: {}
  };

  var storedState = core.getStorageItem(STORAGE_KEY);
  var legacyState = !storedState && !core.isAccountSyncActive() && core.LEGACY_STORAGE_KEYS
    ? core.getStorageItem(core.LEGACY_STORAGE_KEYS.projects)
    : null;
  var state = sanitizeState(storedState
    ? core.readVersionedState(STORAGE_KEY, initialState)
    : core.safeJsonParse(legacyState, initialState));

  if (!storedState && legacyState) {
    state = sanitizeState(core.writeVersionedState(STORAGE_KEY, state));
  }

  function sanitizeSection(section) {
    return {
      id: core.toStringValue(section && section.id) || core.uuid(),
      name: core.toStringValue(section && section.name) || 'Room',
      createdAt: Number(section && section.createdAt) || core.now(),
      items: Array.isArray(section && section.items) ? section.items.map(sanitizeProjectItem).filter(Boolean) : []
    };
  }

  function sanitizeProjectItem(item) {
    if (!item || typeof item !== 'object') return null;

    var reference = core.createReference(item.reference || item.product || item);
    if (!reference) return null;

    return {
      id: core.toStringValue(item.id) || core.uuid(),
      reference: reference,
      requirement: reference.type === 'scene' ? '' : core.toStringValue(item.requirement),
      note: core.toStringValue(item.note),
      addedAt: Number(item.addedAt) || core.now()
    };
  }

  function sanitizeProject(project) {
    var nextProject = {
      id: core.toStringValue(project && project.id) || core.uuid(),
      name: core.toStringValue(project && project.name) || 'Untitled Project',
      createdAt: Number(project && project.createdAt) || core.now(),
      updatedAt: Number(project && project.updatedAt) || core.now(),
      sections: Array.isArray(project && project.sections) ? project.sections.map(sanitizeSection) : [],
      unassignedItems: Array.isArray(project && project.unassignedItems) ? project.unassignedItems.map(sanitizeProjectItem).filter(Boolean) : []
    };

    var seenReferences = {};

    function dedupeItems(items) {
      return items.filter(function (item) {
        var key = core.getReferenceKey(item.reference);
        if (!key || seenReferences[key]) return false;
        seenReferences[key] = true;
        return true;
      });
    }

    nextProject.unassignedItems = dedupeItems(nextProject.unassignedItems);
    nextProject.sections.forEach(function (section) {
      section.items = dedupeItems(section.items);
    });

    return nextProject;
  }

  function sanitizeState(source) {
    var next = Object.assign({}, initialState, source || {});
    var seenProjects = {};
    var sourceTombstones = next.tombstones && typeof next.tombstones === 'object' && !Array.isArray(next.tombstones)
      ? next.tombstones
      : {};
    var cleanTombstones = {};

    Object.keys(sourceTombstones).forEach(function (projectId) {
      var deletedAt = Number(sourceTombstones[projectId]);
      if (projectId && Number.isFinite(deletedAt) && deletedAt > 0) cleanTombstones[projectId] = deletedAt;
    });

    next.projects = Array.isArray(next.projects)
      ? next.projects.map(sanitizeProject).filter(function (project) {
          if (seenProjects[project.id]) return false;
          if (cleanTombstones[project.id] >= project.updatedAt) return false;
          seenProjects[project.id] = true;
          return true;
        })
      : [];
    next.tombstones = cleanTombstones;
    next.version = core.STORAGE_VERSION;
    next.updatedAt = Number(next.updatedAt) || 0;
    return next;
  }

  function getAll() {
    return core.clone(state.projects);
  }

  function getSnapshot() {
    return {
      version: state.version,
      updatedAt: state.updatedAt,
      projects: getAll(),
      tombstones: core.clone(state.tombstones)
    };
  }

  function notify(source) {
    var payload = {
      projects: getAll(),
      count: state.projects.length,
      updatedAt: state.updatedAt,
      source: source || 'internal'
    };

    emitter.emit(payload);
    core.dispatchDocumentEvent(CHANGE_EVENT, payload);
  }

  function persist(source) {
    state = sanitizeState(core.writeVersionedState(STORAGE_KEY, state));
    notify(source || 'internal');
    return getSnapshot();
  }

  function touchProject(project) {
    project.updatedAt = core.now();
    return project;
  }

  function findProject(projectId) {
    return state.projects.find(function (project) {
      return project.id === projectId;
    }) || null;
  }

  function get(projectId) {
    var project = findProject(projectId);
    return project ? core.clone(project) : null;
  }

  function create(name) {
    var project = sanitizeProject({
      id: core.uuid(),
      name: name || 'Untitled Project',
      createdAt: core.now(),
      updatedAt: core.now(),
      sections: [],
      unassignedItems: []
    });

    state.projects.unshift(project);
    delete state.tombstones[project.id];
    persist('create');
    return core.clone(project);
  }

  function rename(projectId, name) {
    var project = findProject(projectId);
    if (!project) return null;

    project.name = core.toStringValue(name) || project.name;
    touchProject(project);
    persist('rename');
    return core.clone(project);
  }

  function deleteProject(projectId) {
    var index = state.projects.findIndex(function (project) {
      return project.id === projectId;
    });

    if (index === -1) return false;

    state.tombstones[projectId] = core.now();
    state.projects.splice(index, 1);
    persist('delete');
    return true;
  }

  function addSection(projectId, name) {
    var project = findProject(projectId);
    if (!project) return null;

    var section = sanitizeSection({
      id: core.uuid(),
      name: name || 'Room',
      createdAt: core.now(),
      items: []
    });

    project.sections.push(section);
    touchProject(project);
    persist('add-section');
    return core.clone(section);
  }

  function renameSection(projectId, sectionId, name) {
    var project = findProject(projectId);
    if (!project) return null;

    var section = project.sections.find(function (entry) {
      return entry.id === sectionId;
    });

    if (!section) return null;

    section.name = core.toStringValue(name) || section.name;
    touchProject(project);
    persist('rename-section');
    return core.clone(section);
  }

  function deleteSection(projectId, sectionId, options) {
    var project = findProject(projectId);
    if (!project) return false;

    var index = project.sections.findIndex(function (entry) {
      return entry.id === sectionId;
    });

    if (index === -1) return false;

    var section = project.sections[index];
    var settings = Object.assign({ moveItemsToUnassigned: true }, options || {});

    if (settings.moveItemsToUnassigned && section.items.length) {
      project.unassignedItems = project.unassignedItems.concat(section.items);
    }

    project.sections.splice(index, 1);
    touchProject(project);
    persist('delete-section');
    return true;
  }

  function getAllProjectItems(project) {
    var items = project.unassignedItems.slice();

    project.sections.forEach(function (section) {
      items = items.concat(section.items);
    });

    return items;
  }

  function hasProjectReference(project, reference) {
    var key = core.getReferenceKey(reference);

    return getAllProjectItems(project).some(function (item) {
      return core.getReferenceKey(item.reference) === key;
    });
  }

  function addItem(projectId, input, targetSectionId) {
    var project = findProject(projectId);
    if (!project) return null;

    var reference = core.createReference(input);
    if (!reference) return null;
    if (hasProjectReference(project, reference)) return null;

    var item = sanitizeProjectItem({
      id: core.uuid(),
      reference: reference,
      requirement: '',
      note: '',
      addedAt: core.now()
    });

    if (targetSectionId) {
      var section = project.sections.find(function (entry) {
        return entry.id === targetSectionId;
      });

      if (section) {
        section.items.push(item);
      } else {
        project.unassignedItems.push(item);
      }
    } else {
      project.unassignedItems.push(item);
    }

    touchProject(project);
    persist('add-item');
    return core.clone(item);
  }

  function addItems(projectId, inputs, targetSectionId) {
    var project = findProject(projectId);
    if (!project || !Array.isArray(inputs)) return [];

    var target = project.unassignedItems;
    if (targetSectionId) {
      var targetSection = project.sections.find(function (entry) {
        return entry.id === targetSectionId;
      });
      if (targetSection) target = targetSection.items;
    }

    var added = [];
    inputs.forEach(function (input) {
      var reference = core.createReference(input);
      if (!reference || hasProjectReference(project, reference)) return;

      var item = sanitizeProjectItem({
        id: core.uuid(),
        reference: reference,
        requirement: '',
        note: '',
        addedAt: core.now()
      });

      target.push(item);
      added.push(core.clone(item));
    });

    if (!added.length) return [];
    touchProject(project);
    persist('add-items');
    return added;
  }

  function locateItem(project, itemId) {
    var index = project.unassignedItems.findIndex(function (item) {
      return item.id === itemId;
    });

    if (index > -1) {
      return {
        container: project.unassignedItems,
        item: project.unassignedItems[index],
        index: index,
        sectionId: null
      };
    }

    for (var sectionIndex = 0; sectionIndex < project.sections.length; sectionIndex += 1) {
      var section = project.sections[sectionIndex];
      var itemIndex = section.items.findIndex(function (item) {
        return item.id === itemId;
      });

      if (itemIndex > -1) {
        return {
          container: section.items,
          item: section.items[itemIndex],
          index: itemIndex,
          sectionId: section.id
        };
      }
    }

    return null;
  }

  function removeItem(projectId, itemId) {
    var project = findProject(projectId);
    if (!project) return false;

    var located = locateItem(project, itemId);
    if (!located) return false;

    located.container.splice(located.index, 1);
    touchProject(project);
    persist('remove-item');
    return true;
  }

  function moveItem(projectId, itemId, sectionId) {
    var project = findProject(projectId);
    if (!project) return false;

    var located = locateItem(project, itemId);
    if (!located) return false;

    if (located.sectionId === sectionId) return true;

    located.container.splice(located.index, 1);

    if (!sectionId) {
      project.unassignedItems.push(located.item);
    } else {
      var section = project.sections.find(function (entry) {
        return entry.id === sectionId;
      });

      if (section) {
        section.items.push(located.item);
      } else {
        project.unassignedItems.push(located.item);
      }
    }

    touchProject(project);
    persist('move-item');
    return true;
  }

  function updateItemField(projectId, itemId, field, value, source) {
    var project = findProject(projectId);
    if (!project) return null;

    var located = locateItem(project, itemId);
    if (!located) return null;

    located.item[field] = core.toStringValue(value);
    touchProject(project);
    persist(source);
    return core.clone(located.item);
  }

  function updateItemNote(projectId, itemId, value) {
    return updateItemField(projectId, itemId, 'note', value, 'update-note');
  }

  function updateItemRequirement(projectId, itemId, value) {
    var project = findProject(projectId);
    var located = project && locateItem(project, itemId);
    if (!located || core.getReferenceType(located.item.reference) === 'scene') return null;
    return updateItemField(projectId, itemId, 'requirement', value, 'update-requirement');
  }

  function hasItem(projectId, input) {
    var project = findProject(projectId);
    var reference = core.createReference(input);
    return Boolean(project && reference && hasProjectReference(project, reference));
  }

  function refreshReference(input) {
    var reference = core.createReference(input);
    if (!reference || reference.type !== 'scene') return getSnapshot();
    var key = core.getReferenceKey(reference);
    var changed = false;

    state.projects.forEach(function (project) {
      getAllProjectItems(project).forEach(function (item) {
        if (core.getReferenceKey(item.reference) !== key) return;
        var next = Object.assign({}, reference, { addedAt: item.reference.addedAt });
        if (JSON.stringify(item.reference) === JSON.stringify(next)) return;
        item.reference = next;
        touchProject(project);
        changed = true;
      });
    });

    return changed ? persist('refresh-reference') : getSnapshot();
  }

  function replaceSnapshot(snapshot, source) {
    state = sanitizeState(snapshot);
    core.setStorageItem(STORAGE_KEY, JSON.stringify(state));
    notify(source || 'sync');
    return getSnapshot();
  }

  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;

    state = sanitizeState(core.safeJsonParse(event.newValue, initialState));
    notify('storage');
  });

  window.MCubeProjects = {
    CHANGE_EVENT: CHANGE_EVENT,
    addItem: addItem,
    addItems: addItems,
    addSection: addSection,
    create: create,
    createProductReference: core.createProductReference,
    createSceneReference: core.createSceneReference,
    delete: deleteProject,
    deleteSection: deleteSection,
    get: get,
    getAll: getAll,
    getSnapshot: getSnapshot,
    hasItem: hasItem,
    moveItem: moveItem,
    refreshReference: refreshReference,
    replaceSnapshot: replaceSnapshot,
    removeItem: removeItem,
    rename: rename,
    renameSection: renameSection,
    subscribe: emitter.subscribe,
    updateItemNote: updateItemNote,
    updateItemRequirement: updateItemRequirement
  };

  window.ProjectStore = window.MCubeProjects;
})();
